import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { InvoiceDetail, QuoteDetail } from '@techgitter/contracts';
import { fetchInvoiceDetail } from '../../services/invoicesApi';
import { linkInvoice } from '../../services/quotesApi';

interface LinkInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  quote: QuoteDetail;
  onLinked: () => void;
}

const fmt = (n: number): string =>
  n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

const DetailRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ textAlign: 'right' }}>
      {value}
    </Typography>
  </Stack>
);

export const LinkInvoiceDialog: React.FC<LinkInvoiceDialogProps> = ({
  open,
  onClose,
  quote,
  onLinked,
}) => {
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [looking, setLooking] = useState(false);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setInvoiceNumber('');
    setInvoice(null);
    setError(null);
  };

  const handleClose = () => {
    if (looking || linking) return;
    reset();
    onClose();
  };

  const handleLookup = async () => {
    const num = invoiceNumber.trim();
    if (!num) return;
    setLooking(true);
    setError(null);
    setInvoice(null);
    try {
      const inv = await fetchInvoiceDetail(num);
      setInvoice(inv);
    } catch {
      setError(
        `Invoice ${num} wasn’t found. Check the number — it must already exist and be synced to SM4RT.`,
      );
    } finally {
      setLooking(false);
    }
  };

  const handleConfirm = async () => {
    if (!invoice) return;
    setLinking(true);
    setError(null);
    try {
      await linkInvoice(quote.number, invoice.number);
      onLinked();
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLinking(false);
    }
  };

  const customerMismatch =
    invoice &&
    quote.customerId &&
    invoice.customerId &&
    invoice.customerId !== quote.customerId;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link an existing invoice</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
          Link this quote to an invoice you raised manually (not through SM4RT).
          No line items are added — the quote is simply marked as invoiced and
          locked.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            label="Invoice number"
            value={invoiceNumber}
            onChange={(e) => {
              setInvoiceNumber(e.target.value);
              setInvoice(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleLookup();
            }}
            disabled={looking || linking}
            size="small"
            fullWidth
            autoFocus
          />
          <Button
            variant="outlined"
            onClick={handleLookup}
            disabled={!invoiceNumber.trim() || looking || linking}
          >
            {looking ? <CircularProgress size={16} /> : 'Look up'}
          </Button>
        </Box>

        {invoice && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              mb: 1,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 0.5 }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Invoice {invoice.number}
              </Typography>
              <Chip
                size="small"
                color={invoice.isPaid ? 'success' : 'default'}
                label={invoice.isPaid ? 'Paid' : 'Unpaid'}
              />
            </Stack>
            {invoice.name && (
              <Typography variant="body2" color="text.secondary">
                {invoice.name}
              </Typography>
            )}
            <Divider sx={{ my: 1 }} />
            <Stack spacing={0.5}>
              <DetailRow label="Customer" value={invoice.customerName} />
              <DetailRow label="Total" value={fmt(invoice.total)} />
              {invoice.date && <DetailRow label="Date" value={invoice.date} />}
            </Stack>
            {customerMismatch && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                This invoice’s customer differs from the quote’s customer —
                double-check it’s the right invoice before linking.
              </Alert>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={looking || linking}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!invoice || linking}
        >
          {linking ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Linking…
            </>
          ) : (
            'Link invoice'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkInvoiceDialog;
